import { useEffect } from 'react'
import { InfiniteData, QueryClient, useQuery, useQueryClient } from 'react-query'
import { useAuth } from 'context/authContext'
import { getUnseenMessageCount } from 'services/messages'
import { SELF_TEST_TAG_PREFIX, WEB_PUSH_SHOWN } from 'swPush'

/**
 * The inbox's shared cache: the footer badge reads it on every page, the
 * «پیام‌ها» page writes it when it marks messages seen.
 */
export const UNSEEN_COUNT_KEY = ['messages-unseen-count']
/** Prefix of every filtered list: `['inbox', period, category]`. */
export const INBOX_KEY = 'inbox'
export const LOAN_THREADS_KEY = ['loan-threads']

export const inboxKey = (
    period: TInboxPeriod | undefined,
    category: TInboxCategory | undefined
) => [INBOX_KEY, period ?? 'all', category ?? 'all']

/**
 * The server marked `lastId` and everything older seen; mirror that in every
 * cached filter, so switching filters does not bring a yellow square back.
 */
export const markSeenInCache = (queryClient: QueryClient, lastId: number) =>
    queryClient.setQueriesData<InfiniteData<IInboxPage> | undefined>(
        INBOX_KEY,
        (data) =>
            data && {
                ...data,
                pages: data.pages.map((page) => ({
                    ...page,
                    results: page.results.map((m) =>
                        m.id <= lastId && !m.is_seen ? { ...m, is_seen: true } : m
                    ),
                })),
            }
    )

export const removeFromCache = (queryClient: QueryClient, id: number) =>
    queryClient.setQueriesData<InfiniteData<IInboxPage> | undefined>(
        INBOX_KEY,
        (data) =>
            data && {
                ...data,
                pages: data.pages.map((page) => ({
                    ...page,
                    results: page.results.filter((m) => m.id !== id),
                })),
            }
    )

// The push and its inbox row come out of the same dispatch, but not in the
// same instant; a beat later the row is there to count.
const AFTER_PUSH_MS = 1500

/**
 * Unseen messages, for the footer's «پیام‌ها» tab.
 *
 * Read on another device, a message stops counting here the next time this
 * one looks: every minute while the app is open, and whenever it comes back to
 * the foreground. A web push that lands while the app is open refreshes it at
 * once — the service worker already tells every window it showed one.
 */
export function useUnseenCount() {
    const { user } = useAuth()
    const queryClient = useQueryClient()
    const { data } = useQuery(UNSEEN_COUNT_KEY, getUnseenMessageCount, {
        enabled: Boolean(user),
        staleTime: 20_000,
        refetchInterval: 60_000,
    })

    useEffect(() => {
        if (!user || !('serviceWorker' in navigator)) return
        let timer: number | undefined
        const onMessage = (event: MessageEvent) => {
            const message = event.data
            if (message?.type !== WEB_PUSH_SHOWN) return
            // The settings page's «ارسال اعلان آزمایشی» is not a message.
            if (String(message.tag ?? '').startsWith(SELF_TEST_TAG_PREFIX))
                return
            window.clearTimeout(timer)
            timer = window.setTimeout(() => {
                queryClient.invalidateQueries(UNSEEN_COUNT_KEY)
                queryClient.invalidateQueries(INBOX_KEY)
            }, AFTER_PUSH_MS)
        }
        navigator.serviceWorker.addEventListener('message', onMessage)
        return () => {
            window.clearTimeout(timer)
            navigator.serviceWorker.removeEventListener('message', onMessage)
        }
    }, [user, queryClient])

    return user ? data ?? 0 : 0
}

/** «۹۹+» past 99: the server stops counting at 100. */
export const badgeText = (count: number, format: (n: number) => string) =>
    count > 99 ? `${format(99)}+` : format(count)
